"use client";

import React, { useState, useRef, useEffect, useMemo } from "react";
import { getExampleNumber, parsePhoneNumberFromString, getCountryCallingCode, CountryCode } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import countries from "world-countries";
import ChevronDown from "@/components/bnp-icons/ChevronDown";
import { COUNTRIES, CountryOption } from "@/utils/helpers/getCountriesUtils";

interface PhoneInputProps {
  value: string;
  onChange: (fullPhoneNumber: string, isValid: boolean) => void;
  error?: string;
  inputClass: string;
  label?: string;
  required?: boolean;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  error,
  inputClass,
  label = "Phone Number",
  required = false,
}) => {
  // Default to Nigeria ('NG') or fallback to the first entry in COUNTRIES
  const defaultCountry = COUNTRIES.find((c) => c.code === "NG") || COUNTRIES[0];
  
  const [selectedCountry, setSelectedCountry] = useState<CountryOption>(defaultCountry);
  const [nationalNumber, setNationalNumber] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter countries based on name or dial code search query
  const filteredCountries = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.dialCode.includes(query) ||
        c.code.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Determine standard digit lengths from libphonenumber-js examples
  const exampleNumber = getExampleNumber(selectedCountry.code, examples);
  const expectedLength = exampleNumber ? exampleNumber.nationalNumber.length : 10;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip non-digits and leading zeros for standard dial codes
    const cleaned = e.target.value.replace(/\D/g, "").replace(/^0+/, "");
    const capped = cleaned.slice(0, expectedLength);

    setNationalNumber(capped);

    const rawFullNumber = `${selectedCountry.dialCode}${capped}`;
    const parsed = parsePhoneNumberFromString(rawFullNumber, selectedCountry.code);
    const isValid = parsed ? parsed.isValid() : capped.length === expectedLength;

    onChange(rawFullNumber, isValid);
  };

  const handleCountrySelect = (country: CountryOption) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchQuery("");

    const rawFullNumber = `${country.dialCode}${nationalNumber}`;
    const parsed = parsePhoneNumberFromString(rawFullNumber, country.code);
    const isValid = parsed ? parsed.isValid() : nationalNumber.length === expectedLength;

    onChange(rawFullNumber, isValid);
  };

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && (
        <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative flex w-full">
        {/* Country Selector Dropdown Trigger */}
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-1.5 px-3 h-[44px] bg-gray-50 border border-r-0 border-gray-300 rounded-l-md hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
          >
            <span>{selectedCountry.flag}</span>
            <span>{selectedCountry.dialCode}</span>
            <ChevronDown className="text-gray-500" />
          </button>

          {/* Country Dropdown */}
          {isOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg z-50 py-1">
              {/* Search Field inside Dropdown */}
              <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                <input
                  type="text"
                  placeholder="Search country or code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded focus:outline-none focus:border-[#2D96A7]"
                  autoFocus
                />
              </div>

              {/* Country List */}
              {filteredCountries.length > 0 ? (
                filteredCountries.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleCountrySelect(c)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="flex items-center gap-2 truncate pr-2">
                      <span>{c.flag}</span>
                      <span className="text-gray-800 truncate">{c.name}</span>
                    </span>
                    <span className="text-gray-400 font-mono text-xs shrink-0">{c.dialCode}</span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-xs text-gray-400 text-center">No countries found</div>
              )}
            </div>
          )}
        </div>

        {/* Local Number Input */}
        <input
          type="tel"
          value={nationalNumber}
          onChange={handleNumberChange}
          placeholder={`e.g. ${exampleNumber?.nationalNumber || "8123456789"}`}
          className={`${inputClass} rounded-l-none border-l-0 ${error ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
        />
      </div>
      {error && <span className="text-red-500 text-xs mt-1">{error}</span>}
    </div>
  );
};